import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Input } from '~/components/ui/inputs/Input';
import { SearchableSelect } from '~/components/ui/inputs/SearchableSelect';
import { useSuggestAddresses, resolveAddress } from '~/api/facility';
import { useDebounce } from '~/hooks/useDebounce';
import { useCombobox } from '~/hooks/useCombobox';
import {
  COUNTRY_OPTIONS,
  getStatesForCountryIso3,
  resolvedToFormPatch,
  type AddressFormState,
} from '~/utils/address';

type Props = {
  value: AddressFormState;
  onChange: (next: AddressFormState) => void;
  requireFullAddress?: boolean;
};

export function AddressFields({
  value,
  onChange,
  requireFullAddress = true,
}: Props) {
  const { t } = useTranslation('general');
  const valueRef = useRef(value);
  const externalChangeRef = useRef(0);
  useEffect(() => {
    if (valueRef.current !== value) {
      externalChangeRef.current += 1;
      valueRef.current = value;
    }
  }, [value]);
  const [addressSearchString, setAddressSearchString] = useState('');
  const debouncedSetAddressSearchString = useDebounce(
    setAddressSearchString,
    300
  );

  const addressSuggestions = useSuggestAddresses(addressSearchString);
  const suggestions = addressSuggestions.data?.suggestAddresses ?? [];

  const addressCombobox = useCombobox({
    items: suggestions,
    term: addressSearchString,
    minTermLength: 2,
    onSelect: (suggestion) => handleSuggestionSelect(suggestion.text),
  });

  const stateOptions = value.country
    ? getStatesForCountryIso3(value.country).map((state) => ({
        label: state.name,
        value: state.state_code,
      }))
    : [];
  const hasStates = stateOptions.length > 1;

  function handleField(name: string, fieldValue: string) {
    const next = { ...valueRef.current, [name]: fieldValue };
    if (name === 'country') next.region = '';
    valueRef.current = next;
    onChange(next);
  }

  function handleAddressLine1Change(fieldName: string, fieldValue: string) {
    handleField(fieldName, fieldValue);
    addressCombobox.handleInputChange();
    debouncedSetAddressSearchString(fieldValue);
  }

  async function handleSuggestionSelect(suggestionText: string) {
    const next = { ...valueRef.current, addressLine1: suggestionText };
    valueRef.current = next;
    onChange(next);
    setAddressSearchString('');
    const generation = externalChangeRef.current;

    let result;
    try {
      result = await resolveAddress(suggestionText);
    } catch {
      if (generation === externalChangeRef.current) {
        toast.error(t('address_fields.resolve_error'));
      }
      return;
    }

    if (generation !== externalChangeRef.current) return;

    const resolved = result.resolveAddress;
    if (!resolved) return;

    const merged = resolvedToFormPatch(resolved, {
      ...valueRef.current,
      addressLine1: suggestionText,
    });
    valueRef.current = merged;
    onChange(merged);
  }

  return (
    <>
      <div className="relative">
        <Input
          label={t('address_fields.address_line_1')}
          name="addressLine1"
          value={value.addressLine1}
          onChange={handleAddressLine1Change}
          onFocus={addressCombobox.handleFocus}
          onBlur={addressCombobox.handleBlur}
          onKeyDown={addressCombobox.handleKeyDown}
          placeholder="123 Main Street"
          autoComplete="off"
          required
          {...addressCombobox.inputProps}
        />
        {addressCombobox.isOpen && (
          <ul
            id={addressCombobox.listboxId}
            role="listbox"
            aria-label={t('address_fields.suggestions_label')}
            className="absolute z-10 left-0 right-0 top-full mt-1 bg-neut-bg-alt border border-neut-200 rounded-lg shadow-lg overflow-hidden"
          >
            {suggestions.map((suggestion, index) => (
              <li
                key={suggestion.placeId}
                {...addressCombobox.optionProps(index)}
                onMouseDown={(event) => {
                  event.preventDefault();
                  addressCombobox.select(suggestion);
                }}
                className={`px-4 py-2.5 text-sm text-neut-prim cursor-pointer ${
                  index === addressCombobox.safeActiveIndex
                    ? 'bg-neut-50'
                    : 'hover:bg-neut-50'
                }`}
              >
                {suggestion.text}
              </li>
            ))}
          </ul>
        )}
      </div>
      <Input
        label={t('address_fields.address_line_2')}
        name="addressLine2"
        value={value.addressLine2}
        onChange={handleField}
        placeholder="Suite 100"
      />
      <Input
        label={t('address_fields.city')}
        name="city"
        value={value.city}
        onChange={handleField}
        placeholder="Vancouver"
        required={requireFullAddress}
      />
      <SearchableSelect
        label={t('address_fields.country')}
        name="country"
        value={value.country}
        onChange={handleField}
        options={COUNTRY_OPTIONS}
        placeholder={t('address_fields.select_country')}
        required={requireFullAddress}
      />
      {value.country && hasStates && (
        <SearchableSelect
          label={t('address_fields.state')}
          name="region"
          value={value.region}
          onChange={handleField}
          options={stateOptions}
          placeholder={t('address_fields.select_state')}
          required={requireFullAddress}
        />
      )}
      <Input
        label={t('address_fields.zip_code')}
        name="zipCode"
        value={value.zipCode}
        onChange={handleField}
        placeholder="V6B 1A1"
        required={requireFullAddress}
      />
    </>
  );
}
